import React, { useState } from 'react';
import { Check, Plus, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './PricingPage.scss';

export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
    'yearly'
  );
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleGetStarted = (planName: string, price: number) => {
    if (!isAuthenticated) {
      // Redirect to signup/login with plan info
      navigate('/register', {
        state: {
          selectedPlan: planName,
          price: price,
          billingPeriod: billingPeriod,
        },
      });
    } else {
      // User is authenticated, go to checkout/subscription flow
      // For now, we'll redirect to dashboard with a success message
      // In a real app, this would integrate with Stripe/payment processor
      navigate('/dashboard', {
        state: {
          message: `Starting ${planName} plan setup...`,
          planName: planName,
          price: price,
          billingPeriod: billingPeriod,
        },
      });
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: billingPeriod === 'monthly' ? 15 : 12,
      originalPrice: billingPeriod === 'yearly' ? 15 : null,
      period: 'month',
      billedAs: billingPeriod === 'yearly' ? 'Billed $144 yearly' : null,
      description: 'Perfect for individuals getting started',
      features: [
        'Generate 5 courses per month',
        'Access to all course categories',
        'Basic AI model',
        'Email support',
      ],
      popular: false,
    },
    {
      name: 'Pro',
      price: billingPeriod === 'monthly' ? 39 : 29,
      originalPrice: billingPeriod === 'yearly' ? 39 : null,
      period: 'month',
      billedAs: billingPeriod === 'yearly' ? 'Billed $348 yearly' : null,
      description: 'Best for serious learners and professionals',
      features: [
        'Generate 25 courses per month',
        'Priority course generation',
        'Advanced AI model',
        'Export to PDF',
        'Priority support',
        'Early access to features',
      ],
      popular: true,
    },
    {
      name: 'Unlimited',
      price: billingPeriod === 'monthly' ? 99 : 79,
      originalPrice: billingPeriod === 'yearly' ? 99 : null,
      period: 'month',
      billedAs: billingPeriod === 'yearly' ? 'Billed $948 yearly' : null,
      description: 'For power users and small teams',
      features: [
        'Unlimited course generation',
        'Custom AI model training',
        'Team collaboration tools',
        'White-label options',
        'Dedicated support',
        'Custom integrations',
      ],
      popular: false,
    },
  ];

  return (
    <div className="pricing-page">
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="pricing-hero-content">
          <div className="pricing-hero-badge">Simple, transparent pricing</div>

          <h1 className="pricing-hero-title">Choose your learning plan</h1>

          <p className="pricing-hero-description">
            Start for free, then choose the plan that scales with your education
            goals.
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`toggle-btn ${
                billingPeriod === 'monthly' ? 'active' : ''
              }`}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${
                billingPeriod === 'yearly' ? 'active' : ''
              }`}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly
              <span className="save-badge">Save 25%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pricing-plans">
        <div className="plans-grid">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`pricing-card ${plan.popular ? 'featured' : ''}`}
            >
              {plan.popular && (
                <div className="popular-badge">Most popular</div>
              )}

              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="plan-pricing">
                <div className="price-display">
                  {plan.originalPrice && (
                    <span className="original-price">
                      ${plan.originalPrice}
                    </span>
                  )}
                  <span className="currency">$</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/{plan.period}</span>
                </div>
                {plan.billedAs && (
                  <p className="billing-note">{plan.billedAs}</p>
                )}
              </div>

              <div className="plan-features">
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <Check size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                className={`plan-button ${
                  plan.popular ? 'primary' : 'secondary'
                }`}
                onClick={() => handleGetStarted(plan.name, plan.price)}
              >
                Get started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-header">
          <h2>Frequently asked questions</h2>
          <p>Everything you need to know about our pricing and plans.</p>
        </div>

        <div className="faq-list">
          {[
            {
              question: 'Can I change my plan anytime?',
              answer:
                'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.',
            },
            {
              question: 'What happens if I exceed my course limit?',
              answer:
                'You can either wait for the next billing cycle or upgrade to a higher plan to continue generating courses.',
            },
            {
              question: 'Is there a free trial?',
              answer:
                'Yes! All plans come with a 7-day free trial. No credit card required.',
            },
            {
              question: 'Can I cancel anytime?',
              answer:
                'Absolutely. Cancel your subscription anytime with no hidden fees or penalties.',
            },
          ].map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaqIndex === index}
              >
                <h3>{faq.question}</h3>
                <span className="faq-icon">
                  {openFaqIndex === index ? (
                    <Minus size={20} />
                  ) : (
                    <Plus size={20} />
                  )}
                </span>
              </button>
              <div
                className={`faq-answer ${openFaqIndex === index ? 'open' : 'closed'}`}
              >
                <div
                  className="faq-answer-content"
                  style={{ padding: '0 0 1.5rem 0' }}
                >
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="pricing-page-cta">
        <div className="pricing-cta-container">
          <div className="pricing-cta-content">
            <h2>Ready to start learning?</h2>
            <p>
              Join thousands of learners who are advancing their careers with
              AI-powered education.
            </p>
            <button
              className="btn-primary"
              onClick={() => handleGetStarted('Starter', plans[0].price)}
            >
              Start your free trial
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
